export enum Currency {
  EUR = 'EUR',
  GBP = 'GBP',
  USD = 'USD',
  CAD = 'CAD',
  DKK = 'DKK',
  SEK = 'SEK',
  HUF = 'HUF',
}

export interface EntityConfig {
  hotline: string;
  email: string;
  legalEntityName: string;
  country: string;
  legalEntityId: number;
  region: 'EU' | 'NA';
  languages: string[];
  currency: Currency;
  changeRequestPDF?: {
    de?: string;
    en?: string;
    fr?: string;
    nl?: string;
    it?: string;
    es?: string;
  };
}

const wuddiLegalEntityId = 38;
interface Config {
  [wuddiLegalEntityId]: EntityConfig;
  DE: EntityConfig;
  ES: EntityConfig;
  AT: EntityConfig;
  IT: EntityConfig;
  NL: EntityConfig;
  FR: EntityConfig;
  // PT: EntityConfig;
}

export const salesforceLanguageMap = {
  en: 'English',
  de: 'German',
  da: 'Danish',
  es: 'Spanish',
  it: 'Italian',
  hu: 'Hungarian',
  fr: 'French',
  nl: 'Dutch',
};

export const countryCityMappings = {
  DE: ['Berlin', 'Frankfurt', 'Hamburg', 'Köln / Düsseldorf', 'Munich', 'Münster', 'Stuttgart'],
  AT: ['Vienna'],
  DK: ['Copenhagen'],
  ES: ['Madrid'],
  FR: ['Paris'],
  HU: ['Budapest'],
  IT: ['Milan', 'Rome', 'Turin'],
  NL: ['Amsterdam'],
};

export enum LegalEntityIds {
  GERMANY = 10,
  GERMANY_WUDDI = 38,
  AUSTRIA = 2,
  ITALY = 20,
  DENMARK = 37,
  SPAIN = 23,
  FRANCE = 3,
  HUNGARY = 32,
  NETHERLANDS = 1,
}

export const legalEntityToCity: { [key: number]: string } = {
  [LegalEntityIds.GERMANY_WUDDI]: 'muenster',
};

export const getCountryConfig = (country?: string, legalEntityId?: number) => {
  return AppConfig[legalEntityId!] || AppConfig[country!] || AppConfig[DEFAULT_COUNTRY_CODE];
};
export const DEFAULT_COUNTRY_CODE: 'DE' | 'ES' | 'AT' | 'IT' | 'NL' | 'FR' | 'HU' | 'DK' = 'DE';

export const legalEntityToCountryCodeMap: { [key: number]: string } = {
  [LegalEntityIds.GERMANY]: 'DE',
  [LegalEntityIds.GERMANY_WUDDI]: 'WU',
  [LegalEntityIds.SPAIN]: 'ES',
  [LegalEntityIds.AUSTRIA]: 'AT',
  [LegalEntityIds.ITALY]: 'IT',
  [LegalEntityIds.NETHERLANDS]: 'NL',
  [LegalEntityIds.FRANCE]: 'FR',
  [LegalEntityIds.HUNGARY]: 'HU',
  [LegalEntityIds.DENMARK]: 'DK',
};

export const legalDocumentLinks = {
  privacyPolicy: {
    at: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14504.pdf',
    de: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14505.pdf',
    es: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14503.pdf',
    it: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14508.pdf',
    nl: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14506.pdf',
    fr: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14507.pdf',
  },
  termsAndConditions: {
    de_at: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14495.pdf',
    en_at: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14495.pdf',
    de_de: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14500.pdf',
    en_de: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14501.pdf',
    es_es: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14493.pdf',
    en_es: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14492.pdf',
    it_it: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14494.pdf',
    en_it: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14491.pdf',
    nl_nl: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14499.pdf',
    en_nl: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14498.pdf',
    fr_fr: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14497.pdf',
    en_fr: 'https://share-now.assetbank-server.com/assetbank-share-now/assetfile/14496.pdf',
  },
};

// tslint:disable:object-literal-sort-keys
export const AppConfig: Config = {
  DE: {
    hotline: '+49 (0)30 233 40 110',
    email: 'business.de@free2move.com',
    legalEntityName: 'Free2move Deutschland GmbH',
    legalEntityId: 10,
    region: 'EU',
    languages: ['de', 'en'],
    currency: Currency.EUR,
    country: 'Germany',
    changeRequestPDF: {
      de: 'https://b2b-sales-dev.s3.eu-central-1.amazonaws.com/company-change-requests/Business_Account_App._DE_DE_SEPA.pdf',
    },
  },
  [wuddiLegalEntityId]: {
    hotline: '+49 (0)30 233 40 110',
    email: ' business@wuddi.free2move.com',
    legalEntityName: 'wuddi GmbH',
    legalEntityId: wuddiLegalEntityId,
    region: 'EU',
    languages: ['de', 'en'],
    currency: Currency.EUR,
    country: 'Germany',
  },
  ES: {
    hotline: '+34911309473',
    email: 'business.es@free2move.com',
    legalEntityName: 'Free2move Iberia S.A.U.',
    legalEntityId: 23,
    region: 'EU',
    languages: ['es', 'en'],
    currency: Currency.EUR,
    country: 'Spain',
    changeRequestPDF: {
      es: 'https://b2b-sales-dev.s3.eu-central-1.amazonaws.com/company-change-requests/Business_Account_App._ES_ES.pdf',
    },
  },
  AT: {
    hotline: '+4312677111',
    email: 'business.at@free2move.com',
    legalEntityName: 'Free2move Österreich GmbH',
    legalEntityId: 2,
    region: 'EU',
    languages: ['de', 'en'],
    currency: Currency.EUR,
    country: 'Austria',
    changeRequestPDF: {
      de: 'https://b2b-sales-dev.s3.eu-central-1.amazonaws.com/company-change-requests/Business_Account_App._DE_AT_SEPA.pdf',
    },
  },
  IT: {
    hotline: '+390260063093',
    email: 'business.it@free2move.com',
    legalEntityName: 'Free2move Italia S.r.l.',
    legalEntityId: 20,
    region: 'EU',
    languages: ['it', 'en'],
    currency: Currency.EUR,
    country: 'Italy',
    changeRequestPDF: {
      it: 'https://b2b-sales-dev.s3.eu-central-1.amazonaws.com/company-change-requests/SHARE_NOW_Business_Account_Application_Form_IT_IT_07.01.2020.pdf',
    },
  },
  NL: {
    hotline: '+31203228989',
    email: 'business.nl@free2move.com',
    legalEntityName: 'Free2move Nederland B.V.',
    legalEntityId: 1,
    region: 'EU',
    languages: ['nl', 'en'],
    currency: Currency.EUR,
    country: 'Netherlands',
    changeRequestPDF: {
      nl: 'https://b2b-sales-dev.s3.eu-central-1.amazonaws.com/company-change-requests/Business_Account_App._NL_NL_SEPA.pdf',
    },
  },
  FR: {
    hotline: '+33187211998',
    email: 'business.fr@free2move.com',
    legalEntityName: 'Free2move SAS',
    legalEntityId: 3,
    region: 'EU',
    languages: ['fr', 'en'],
    currency: Currency.EUR,
    country: 'France',
    changeRequestPDF: {
      fr: 'https://b2b-sales-dev.s3.eu-central-1.amazonaws.com/company-change-requests/Business_Account_App._FR_FR_.pdf',
    },
  },
  // Portugal has been delisted
  // PT: {
  //   hotline: '+33187211998',
  //   email: 'business.pt@share-now.com',
  //   legalEntityName: 'Via Verde Carsharing, S.A.',
  //   legalEntityId: 28,
  //   region: 'EU',
  //   languages: ['pt', 'en'],
  //   currency: Currency.EUR,
  //   country: 'Portugal',
  // },
};
